import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import {
  Newspaper, Plus, Pin, PinOff, Trash2, Send, Pencil, Save, Camera, Upload, Download,
  Bold, Italic, List as ListIcon, Type, Link2, Video, Film, MessageSquare, Settings, X, UserCircle, Loader2, Search
} from 'lucide-react';
import { Modal, SearchBar, matchesSearch, EMOJI_CATS, formatDate, getRoleBadgeClass, getRoleLabel, AvatarWithCard, uploadFileChunked, UploadProgress, prepareImageFile, confirmDialog, alertDialog, SafeHtml } from './shared';

export default function NewsFeedTab({
  user, authHeaders, API_URL, isAdminOrCoach,
  news, refreshNews, categories, fetchCategories,
  allUsers, downline, searchTerm, setSearchTerm, focusPost
}) {
  const [newPost, setNewPost] = useState('');
  const [newPostImages, setNewPostImages] = useState([]);
  const [newPostVideos, setNewPostVideos] = useState([]);
  const [newPostCategory, setNewPostCategory] = useState('Allgemein');
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostVideo, setNewPostVideo] = useState('');
  const [showPostForm, setShowPostForm] = useState(false);
  const [newsCategory, setNewsCategory] = useState('Alle');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiTarget, setEmojiTarget] = useState('content');
  const [emojiCat, setEmojiCat] = useState('Smileys');
  const [editingPost, setEditingPost] = useState(null);
  const [editPostContent, setEditPostContent] = useState('');
  const [editPostTitle, setEditPostTitle] = useState('');
  const [editPostImages, setEditPostImages] = useState([]);
  const [editPostVideos, setEditPostVideos] = useState([]);
  const [editPostVideo, setEditPostVideo] = useState('');
  const [editPostFiles, setEditPostFiles] = useState([]);
  const [editPostCategory, setEditPostCategory] = useState('');
  const [commentText, setCommentText] = useState({});
  const [commentFiles, setCommentFiles] = useState({});
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentFiles, setEditCommentFiles] = useState([]);
  const [commentEmojiFor, setCommentEmojiFor] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [expandedPosts, setExpandedPosts] = useState({});
  const [uploadingNewsImage, setUploadingNewsImage] = useState(false);
  const [newPostFiles, setNewPostFiles] = useState([]);
  const [uploadProg, setUploadProg] = useState(null);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const categoryNames = ['Alle', ...categories.map(c => c.name)];
  const [lightbox, setLightbox] = useState(null);

  // Entwurf-Sicherung: schuetzt vor Textverlust bei Tab-/Fensterwechsel (Safari verwirft Seiten im Hintergrund)
  const DRAFT_KEY = 'fm_news_draft';

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      const hasContent = d && ((d.content || '').replace(/<[^>]*>/g, '').trim() || (d.title || '').trim() || (d.images || []).length || (d.videos || []).length || (d.files || []).length);
      if (!hasContent) return;
      setNewPost(d.content || '');
      setNewPostTitle(d.title || '');
      setNewPostImages(d.images || []);
      setNewPostVideos(d.videos || []);
      setNewPostVideo(d.ytVideo || '');
      setNewPostFiles(d.files || []);
      if (d.category) setNewPostCategory(d.category);
      setShowPostForm(true);
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!showPostForm) return;
    const hasContent = (newPost || '').replace(/<[^>]*>/g, '').trim() || newPostTitle.trim() || newPostImages.length || newPostVideos.length || newPostFiles.length;
    try {
      if (hasContent) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ content: newPost, title: newPostTitle, images: newPostImages, videos: newPostVideos, ytVideo: newPostVideo, files: newPostFiles, category: newPostCategory, ts: Date.now() }));
      } else {
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch (e) { /* ignore */ }
  }, [newPost, newPostTitle, newPostImages, newPostVideos, newPostVideo, newPostFiles, newPostCategory, showPostForm]);

  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ } };

  const getPostImages = (post) => (post.image_urls && post.image_urls.length ? post.image_urls : (post.image_url ? [post.image_url] : []));
  const getPostVideos = (post) => (post.video_urls || []);
  const resolveUrl = (u) => (u && u.startsWith('/') ? `${API_URL}${u}` : u);

  // Jump to a post when opened via notification
  useEffect(() => {
    if (!focusPost?.id) return;
    if (focusPost.type === 'post_comment' || focusPost.type === 'comment_like') {
      setExpandedComments(prev => ({ ...prev, [focusPost.id]: true }));
    }
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="news-${focusPost.id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('news-focus-highlight');
        setTimeout(() => el.classList.remove('news-focus-highlight'), 2600);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [focusPost]);

  // Build user profile image map - combine API data with local data
  const userImgMap = {};
  if (user) userImgMap[user.id] = user.profile_image;
  (allUsers || []).forEach(u => { if (u.profile_image) userImgMap[u.id] = u.profile_image; });
  (downline || []).forEach(u => { if (u.profile_image) userImgMap[u.id] = u.profile_image; });
  const getAuthorImg = (authorId, postOrComment) => {
    // Prefer author_image from API (works for all users), fallback to local map
    if (postOrComment?.author_image) return postOrComment.author_image;
    return userImgMap[authorId] || null;
  };

  const localRefreshNews = async (cat) => {
    const catToUse = cat !== undefined ? cat : newsCategory;
    try {
      const catParam = catToUse !== 'Alle' ? `?category=${catToUse}` : '';
      const resp = await axios.get(`${API_URL}/api/community/news${catParam}`, { headers: authHeaders });
      refreshNews(resp.data.posts || []);
    } catch (e) { console.error(e); }
  };

  const handleCategoryChange = (cat) => {
    setNewsCategory(cat);
    localRefreshNews(cat);
  };

  const renderContent = (text) => {
    if (!text) return '';
    // Content from WYSIWYG editor is already HTML - pass through safely
    let html = text
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Strip scripts
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" style="color:#C8A45C">$1</a>');
    // DOM-based auto-linking: wraps plain URLs in text nodes, skips URLs already inside <a>
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    const urlRe = /https?:\/\/[^\s<>"']+/g;
    textNodes.forEach(node => {
      if (node.parentElement && node.parentElement.closest('a')) return;
      const val = node.nodeValue;
      if (!/https?:\/\//.test(val)) return;
      const frag = document.createDocumentFragment();
      let last = 0, m;
      urlRe.lastIndex = 0;
      while ((m = urlRe.exec(val)) !== null) {
        let url = m[0];
        const trail = url.match(/[.,;:!?)]+$/);
        const trimmed = trail ? trail[0] : '';
        if (trimmed) url = url.slice(0, -trimmed.length);
        if (m.index > last) frag.appendChild(document.createTextNode(val.slice(last, m.index)));
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noreferrer';
        a.style.color = '#C8A45C';
        a.textContent = url;
        frag.appendChild(a);
        if (trimmed) frag.appendChild(document.createTextNode(trimmed));
        last = m.index + m[0].length;
      }
      if (last < val.length) frag.appendChild(document.createTextNode(val.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });
    tpl.content.querySelectorAll('a').forEach(a => {
      a.target = '_blank';
      a.rel = 'noreferrer';
      if (!a.style.color) a.style.color = '#C8A45C';
    });
    return tpl.innerHTML;
  };

  const getYoutubeEmbed = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const getYoutubeThumbnail = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
  };

  const handlePostNews = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    try {
      await axios.post(`${API_URL}/api/community/news`, {
        title: newPostTitle, content: newPost, image_url: '', image_urls: newPostImages, video_url: newPostVideo, video_urls: newPostVideos, files: newPostFiles, category: newPostCategory,
      }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      clearDraft();
      setNewPost(''); setNewPostImages([]); setNewPostVideos([]); setNewPostCategory('Allgemein'); setNewPostTitle(''); setNewPostVideo(''); setShowEmojiPicker(false); setShowPostForm(false); setNewPostFiles([]);
      const editor = document.getElementById('news-content-editor'); if (editor) editor.innerHTML = '';
      localRefreshNews();
    } catch (e) { console.error(e); }
  };

  const handleUploadNewsImage = async (e, isEdit = false) => {
    const files = Array.from(e.target.files || []);
    const input = e.target;
    if (!files.length) return;
    setUploadingNewsImage(true);
    for (const file of files) {
      let toUpload;
      try { toUpload = await prepareImageFile(file); } catch (err) { alertDialog(err.message, { title: 'Bild nicht geeignet', danger: true }); continue; }
      try {
        const fd = new FormData(); fd.append('file', toUpload);
        const resp = await axios.post(`${API_URL}/api/community/news/upload-image`, fd, {
          headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
          onUploadProgress: ev => setUploadProg({ name: toUpload.name, loaded: ev.loaded, total: ev.total || toUpload.size }),
        });
        if (isEdit) setEditPostImages(prev => [...prev, resp.data.image_url]);
        else setNewPostImages(prev => [...prev, resp.data.image_url]);
      } catch (err) { alertDialog(err.response?.data?.detail || 'Upload fehlgeschlagen', { title: 'Upload fehlgeschlagen', danger: true }); }
    }
    setUploadProg(null);
    setUploadingNewsImage(false);
    input.value = '';
  };

  const handleUploadNewsVideo = (isEdit = false) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/webm,video/quicktime,video/x-m4v,video/ogg';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingNewsImage(true);
      try {
        const resp = await uploadFileChunked(file, API_URL, authHeaders, setUploadProg);
        const item = { url: resp.file_url, name: resp.file_name };
        if (isEdit) setEditPostVideos(prev => [...prev, item]);
        else setNewPostVideos(prev => [...prev, item]);
      } catch (err) { alertDialog(err.response?.data?.detail || 'Video-Upload fehlgeschlagen. Bitte erneut versuchen.', { title: 'Video-Upload fehlgeschlagen', danger: true }); }
      setUploadProg(null);
      setUploadingNewsImage(false);
    };
    input.click();
  };

  const handleLikeNews = async (postId) => {
    try {
      await axios.post(`${API_URL}/api/community/news/${postId}/like`, {}, { headers: authHeaders });
      localRefreshNews();
    } catch (e) { console.error(e); }
  };

  const handleCommentNews = async (postId) => {
    const editorEl = document.getElementById(`comment-editor-${postId}`);
    const html = editorEl ? editorEl.innerHTML : '';
    const plain = editorEl ? editorEl.innerText.trim() : '';
    const files = commentFiles[postId] || [];
    if (!plain && files.length === 0) return;
    try {
      await axios.post(`${API_URL}/api/community/news/${postId}/comments`, { text: html, files }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      if (editorEl) editorEl.innerHTML = '';
      setCommentText(prev => ({ ...prev, [postId]: '' }));
      setCommentFiles(prev => ({ ...prev, [postId]: [] }));
      setCommentEmojiFor(null);
      localRefreshNews();
    } catch (e) { alertDialog(e.response?.data?.detail || 'Kommentar konnte nicht gesendet werden. Bitte erneut versuchen.', { title: 'Fehler', danger: true }); }
  }; 

  const handleLikeComment = async (postId, commentId) => {
    try {
      await axios.post(`${API_URL}/api/community/news/${postId}/comments/${commentId}/like`, {}, { headers: authHeaders });
      localRefreshNews();
    } catch (e) { console.error(e); }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!(await confirmDialog('Kommentar wirklich löschen?', { title: 'Kommentar löschen' }))) return;
    try {
      await axios.delete(`${API_URL}/api/community/news/${postId}/comments/${commentId}`, { headers: authHeaders });
      localRefreshNews();
    } catch (e) { alertDialog(e.response?.data?.detail || 'Löschen fehlgeschlagen', { title: 'Fehler', danger: true }); }
  };

  const handleSaveCommentEdit = async (postId, commentId) => {
    const el = document.getElementById(`edit-comment-editor-${commentId}`);
    const html = el ? el.innerHTML : '';
    const plain = el ? el.innerText.trim() : '';
    if (!plain && editCommentFiles.length === 0) return;
    try {
      await axios.put(`${API_URL}/api/community/news/${postId}/comments/${commentId}`, { text: html, files: editCommentFiles }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setEditingComment(null);
      setCommentEmojiFor(null);
      localRefreshNews();
    } catch (e) { alertDialog(e.response?.data?.detail || 'Bearbeiten fehlgeschlagen', { title: 'Fehler', danger: true }); }
  };

  const canEditComment = (c) => c.author_id === user?.id && (Date.now() - new Date(c.created_at).getTime()) < 3600000;
  const isImgFile = (f) => /\.(jpe?g|png|webp|gif)$/i.test(f?.name || f?.url || '');

  const addCommentFile = (postId, f, isEdit) => {
    if (isEdit) setEditCommentFiles(prev => [...prev, f]);
    else setCommentFiles(prev => ({ ...prev, [postId]: [...(prev[postId] || []), f] }));
  };

  const removeCommentFile = (postId, idx, isEdit) => {
    if (isEdit) setEditCommentFiles(prev => prev.filter((_, j) => j !== idx));
    else setCommentFiles(prev => ({ ...prev, [postId]: (prev[postId] || []).filter((_, j) => j !== idx) }));
  };

  const renderCommentToolbar = (editorId, postId, isEdit) => (
    <div className="comment-format-bar">
      <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('bold')} title="Fett"><Bold size={13} /></button>
      <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('italic')} title="Kursiv"><Italic size={13} /></button>
      <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('insertUnorderedList')} title="Aufzählung"><ListIcon size={13} /></button>
      <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => openUrlDialog('link', editorId, 'comment')} title="Link einfügen" data-testid={`comment-link-btn-${editorId}`}><Link2 size={13} /></button>
      <button type="button" className="fmt-btn emoji-fmt" onClick={() => setCommentEmojiFor(commentEmojiFor === editorId ? null : editorId)} title="Emoji" data-testid={`comment-emoji-btn-${editorId}`}>😊</button>
      <label className="fmt-btn" title="Bild anhängen">
        <Camera size={13} />
        <input type="file" accept="image/*" hidden data-testid={`comment-image-input-${editorId}`} onChange={async (e) => { const file = e.target.files[0]; const input = e.target; if (!file) return; let toUpload; try { toUpload = await prepareImageFile(file); } catch (perr) { alertDialog(perr.message, { title: 'Bild nicht geeignet', danger: true }); input.value = ''; return; } try { const fd = new FormData(); fd.append('file', toUpload); const resp = await axios.post(`${API_URL}/api/community/news/upload-image`, fd, { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }, onUploadProgress: ev => setUploadProg({ name: toUpload.name, loaded: ev.loaded, total: ev.total || toUpload.size }) }); addCommentFile(postId, { url: resp.data.image_url, name: toUpload.name }, isEdit); } catch (err) { alertDialog(err.response?.data?.detail || 'Upload fehlgeschlagen', { title: 'Upload fehlgeschlagen', danger: true }); } setUploadProg(null); input.value = ''; }} />
      </label>
      <label className="fmt-btn" title="Datei anhängen">
        <Upload size={13} />
        <input type="file" hidden data-testid={`comment-file-input-${editorId}`} onChange={async (e) => { const file = e.target.files[0]; if (!file) return; const input = e.target; try { const resp = await uploadFileChunked(file, API_URL, authHeaders, setUploadProg); addCommentFile(postId, { url: resp.file_url, name: resp.file_name }, isEdit); } catch (err) { alertDialog(err.response?.data?.detail || 'Upload fehlgeschlagen. Bitte erneut versuchen.', { title: 'Upload fehlgeschlagen', danger: true }); } setUploadProg(null); input.value = ''; }} />
      </label>
    </div>
  );

  const renderCommentEmojiPicker = (editorId, postId) => (
    commentEmojiFor === editorId && (
      <div className="emoji-picker-pro comment-emoji" data-testid={`comment-emoji-picker-${editorId}`}>
        <div className="emoji-cat-tabs">
          {Object.keys(EMOJI_CATS).map(cat => (
            <button key={cat} type="button" className={`emoji-cat-tab ${emojiCat === cat ? 'active' : ''}`} onClick={() => setEmojiCat(cat)}>{cat}</button>
          ))}
        </div>
        <div className="emoji-grid">
          {EMOJI_CATS[emojiCat].map(em => (
            <button key={em} type="button" className="emoji-btn" onClick={() => { const ed = document.getElementById(editorId); if (ed) { ed.focus(); document.execCommand('insertText', false, em); if (editorId.startsWith('comment-editor-')) setCommentText(prev => ({ ...prev, [postId]: ed.innerText })); } setCommentEmojiFor(null); }}>{em}</button>
          ))}
        </div>
      </div>
    )
  );

  const renderCommentFilesList = (files, postId, isEdit) => (
    (files || []).length > 0 && (
      <div className="news-files-list">
        {files.map((f, i) => (
          <div key={`cf-${i}-${f.name}`} className="multi-item"><Download size={12} /><span>{f.name}</span><button type="button" className="btn-icon-sm danger" onClick={() => removeCommentFile(postId, i, isEdit)}><X size={12} /></button></div>
        ))}
      </div>
    )
  );

  const handlePinNews = async (postId, pin) => {
    try {
      await axios.put(`${API_URL}/api/community/news/${postId}/${pin ? 'pin' : 'unpin'}`, {}, { headers: authHeaders });
      localRefreshNews();
    } catch (e) { console.error(e); }
  };

  const handleDeleteNews = async (postId) => {
    if (!(await confirmDialog('Beitrag wirklich löschen? Dies kann nicht rückgängig gemacht werden.', { title: 'Beitrag löschen' }))) return;
    try {
      await axios.delete(`${API_URL}/api/community/news/${postId}`, { headers: authHeaders });
      localRefreshNews();
    } catch (e) { console.error(e); }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/community/categories`, { name: newCategoryName }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setNewCategoryName('');
      fetchCategories();
    } catch (e) { console.error(e); }
  };

  const handleDeleteCategory = async (catId) => {
    if (!(await confirmDialog('Kategorie wirklich löschen?', { title: 'Kategorie löschen' }))) return;
    try {
      await axios.delete(`${API_URL}/api/community/categories/${catId}`, { headers: authHeaders });
      fetchCategories();
    } catch (e) { console.error(e); }
  };

  const [urlDialog, setUrlDialog] = useState(null);
  const [urlValue, setUrlValue] = useState('');
  const savedRange = useRef(null);

  const openUrlDialog = (type, editorId = null, isEdit = false) => {
    if (type === 'link') {
      const sel = window.getSelection();
      savedRange.current = sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
    }
    setUrlValue('');
    setUrlDialog({ type, editorId, isEdit });
  };

  const confirmUrlDialog = () => {
    const url = urlValue.trim();
    if (!url || !urlDialog) return;
    if (urlDialog.type === 'video') {
      if (urlDialog.isEdit) setEditPostVideo(url); else setNewPostVideo(url);
    } else {
      const editor = document.getElementById(urlDialog.editorId);
      if (editor) {
        editor.focus();
        if (savedRange.current) {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(savedRange.current);
        }
        document.execCommand('createLink', false, url);
        if (urlDialog.isEdit === true) setEditPostContent(editor.innerHTML);
        else if (urlDialog.isEdit === false) setNewPost(editor.innerHTML);
      }
    }
    setUrlDialog(null);
    setUrlValue('');
  };

  const [showMobileSearch, setShowMobileSearch] = useState(false);

  const filteredNews = news.filter(p => matchesSearch(p, searchTerm, ['content', 'author_name', 'category', 'title']));

  return (
    <div className="tab-content" data-testid="news-content">
      {/* Compact toolbar: Dropdown + Settings + New Post + Search */}
      <div className="news-mobile-toolbar">
        <select value={newsCategory} onChange={e => handleCategoryChange(e.target.value)} className="news-mobile-cat-select" data-testid="news-cat-select">
          {categoryNames.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        {isAdminOrCoach && <button className="news-mobile-manage-btn" onClick={() => setShowManageCategories(true)} title="Kategorien verwalten" data-testid="manage-categories"><Settings size={16} /></button>}
        <button className="news-mobile-action-btn" onClick={() => setShowPostForm(true)} title="Neuen Beitrag" data-testid="open-post-form"><Plus size={18} /></button>
        <button className={`news-mobile-action-btn ${showMobileSearch ? 'active' : ''}`} onClick={() => setShowMobileSearch(!showMobileSearch)} title="Suchen" data-testid="search-toggle"><Search size={18} /></button>
      </div>
      {showMobileSearch && (
        <div style={{ marginBottom: '0.5rem' }}>
          <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Beitraege durchsuchen..." />
        </div>
      )}

      {/* Post form (both desktop & mobile) */}
      {showPostForm && (
        <div className="section-card news-form-card">
          <form onSubmit={handlePostNews} className="news-post-form" data-testid="news-post-form">
            <div className="title-with-emoji">
              <input value={newPostTitle} onChange={e => setNewPostTitle(e.target.value)} placeholder="Titel" className="news-title-input" data-testid="news-title" required />
              <button type="button" className="title-emoji-btn" onClick={() => { setEmojiTarget('title'); setShowEmojiPicker(!showEmojiPicker); }} title="Emoji">😊</button>
            </div>
            <div className="news-format-bar">
              <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('bold')} title="Fett"><Bold size={15} /></button>
              <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('italic')} title="Kursiv"><Italic size={15} /></button>
              <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('insertUnorderedList')} title="Aufzählung"><ListIcon size={15} /></button>
              <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('insertOrderedList')} title="Nummerierung">1.</button>
              <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('formatBlock', false, 'h3')} title="Überschrift"><Type size={15} /></button>
              <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => openUrlDialog('link', 'news-content-editor', false)} title="Link einfuegen" data-testid="insert-link-btn"><Link2 size={15} /></button>
              <button type="button" className="fmt-btn" onClick={() => openUrlDialog('video', null, false)} title="YouTube einbetten" data-testid="insert-video-btn"><Video size={15} /></button>
              <button type="button" className="fmt-btn" onClick={() => handleUploadNewsVideo(false)} title="Video hochladen (max. 1000 MB, wird automatisch auf 720p optimiert)" data-testid="news-upload-video-btn"><Film size={15} /></button>
              <button type="button" className="fmt-btn emoji-fmt" onClick={() => { setEmojiTarget('content'); setShowEmojiPicker(!showEmojiPicker); }} title="Emoji">😊</button>
              <label className="fmt-btn" title="Bilder anhaengen (mehrere moeglich)">
                <Camera size={15} />
                <input type="file" accept="image/*" multiple onChange={handleUploadNewsImage} hidden data-testid="news-image-input" />
              </label>
              <label className="fmt-btn" title="Datei anhaengen">
                <Upload size={15} />
                <input type="file" onChange={async (e) => { const file = e.target.files[0]; if (!file) return; const input = e.target; try { const resp = await uploadFileChunked(file, API_URL, authHeaders, setUploadProg); setNewPostFiles(prev => [...prev, { url: resp.file_url, name: resp.file_name }]); } catch (err) { alertDialog(err.response?.data?.detail || 'Upload fehlgeschlagen. Bitte erneut versuchen.', { title: 'Upload fehlgeschlagen', danger: true }); } setUploadProg(null); input.value = ''; }} hidden />
              </label>
            </div>
            <UploadProgress progress={uploadProg} />
            <div className="news-input-wrap">
              <div id="news-content-editor" className="wysiwyg-editor" contentEditable data-placeholder="Was gibt es Neues?" data-testid="news-input"
                ref={el => { if (el && !el.dataset.init) { if (newPost) el.innerHTML = DOMPurify.sanitize(newPost, { ADD_ATTR: ['target', 'rel'] }); el.dataset.init = '1'; } }}
                onInput={e => setNewPost(e.currentTarget.innerHTML)} onPaste={e => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }} />
              {showEmojiPicker && (emojiTarget === 'title' || emojiTarget === 'content') && (
                <div className="emoji-picker-pro" data-testid="emoji-picker">
                  <div className="emoji-cat-tabs">
                    {Object.keys(EMOJI_CATS).map(cat => (
                      <button key={cat} type="button" className={`emoji-cat-tab ${emojiCat === cat ? 'active' : ''}`} onClick={() => setEmojiCat(cat)}>{cat}</button>
                    ))}
                  </div>
                  <div className="emoji-grid">
                    {EMOJI_CATS[emojiCat].map(em => <button key={em} type="button" className="emoji-btn" onClick={() => { if (emojiTarget === 'title') { setNewPostTitle(prev => prev + em); } else { const editor = document.getElementById('news-content-editor'); if (editor) { editor.focus(); document.execCommand('insertText', false, em); setNewPost(editor.innerHTML); } } setShowEmojiPicker(false); }}>{em}</button>)}
                  </div>
                </div>
              )}
            </div>
            {newPostImages.length > 0 && (
              <div className="news-images-preview" data-testid="news-images-preview">
                {newPostImages.map((img, i) => (
                  <div key={`nimg-${i}`} className="news-image-preview">
                    <img src={resolveUrl(img)} alt="Vorschau" />
                    <button type="button" className="remove-img-btn" onClick={() => setNewPostImages(prev => prev.filter((_, j) => j !== i))} data-testid={`remove-news-image-${i}`}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            {newPostVideos.length > 0 && (
              <div className="news-videos-preview" data-testid="news-videos-preview">
                {newPostVideos.map((v, i) => (
                  <div key={`nvid-${i}`} className="news-video-preview">
                    <span><Film size={13} /> {v.name}</span>
                    <button type="button" className="remove-img-btn" onClick={() => setNewPostVideos(prev => prev.filter((_, j) => j !== i))} data-testid={`remove-news-video-${i}`}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            {newPostVideo && (
              <div className="news-video-preview">
                <span>YouTube: {newPostVideo}</span>
                <button type="button" className="remove-img-btn" onClick={() => setNewPostVideo('')}><X size={14} /></button>
              </div>
            )}
            {newPostFiles.length > 0 && (
              <div className="news-files-list">
                {newPostFiles.map((f, i) => (
                  <div key={`file-${i}-${f.name}`} className="multi-item"><Download size={12} /><span>{f.name}</span><button type="button" className="btn-icon-sm danger" onClick={() => setNewPostFiles(prev => prev.filter((_, j) => j !== i))}><X size={12} /></button></div>
                ))}
              </div>
            )}
            <div className="news-post-toolbar">
              <div className="toolbar-left">
                <select value={newPostCategory} onChange={e => setNewPostCategory(e.target.value)} className="cat-select" data-testid="post-category">
                  {categoryNames.filter(c => c !== 'Alle').map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="toolbar-right">
                <button type="button" className="btn-icon" onClick={() => { clearDraft(); setShowPostForm(false); setNewPost(''); setNewPostTitle(''); setNewPostImages([]); setNewPostVideos([]); setNewPostVideo(''); setNewPostFiles([]); const ed = document.getElementById('news-content-editor'); if(ed) ed.innerHTML=''; }}>Abbrechen</button>
                <button type="submit" className="btn-post" disabled={!newPostTitle.trim() || uploadingNewsImage} data-testid="news-submit">
                  {uploadingNewsImage ? <Loader2 size={16} className="spin" /> : <Send size={16} />} Posten
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {filteredNews.length === 0 ? (
        <div className="empty-state"><Newspaper size={32} /><p>{searchTerm ? 'Keine Treffer.' : 'Noch keine Beitraege.'}</p></div>
      ) : (
        filteredNews.map(post => {
          const embedUrl = getYoutubeEmbed(post.video_url);
          const postImages = getPostImages(post);
          const postVideos = getPostVideos(post);
          const isExpanded = expandedPosts[post.id];
          const isLong = (post.content || '').length > 200;
          const displayContent = isLong && !isExpanded ? post.content.substring(0, 200) + '...' : post.content;
          return (
            <div key={post.id} className={`news-card ${post.pinned ? 'pinned' : ''}`} data-testid={`news-${post.id}`}>
              {post.pinned && <div className="pin-label"><Pin size={12} /> Ankuendigung</div>}
              {post.category && <span className="news-cat-badge">{post.category}</span>}
              <div className="news-header">
                <span className="news-author">
                  <AvatarWithCard userId={post.author_id} image={getAuthorImg(post.author_id, post) ? `${API_URL}${getAuthorImg(post.author_id, post)}` : null} size={24} API_URL={API_URL} authHeaders={authHeaders} avatarClass="news-author-avatar" placeholderClass="news-author-avatar-placeholder" />
                  {post.author_name} <span className={`role-badge ${getRoleBadgeClass(post.author_role)}`}>{getRoleLabel(post.author_role)}</span>
                </span>
                <span className="news-date">{formatDate(post.created_at)}{post.updated_at !== post.created_at ? ' (bearbeitet)' : ''}</span>
              </div>

              {post.title && <h3 className="news-title">{post.title}</h3>}

              {editingPost === post.id ? (
                <div className="news-edit-form" data-testid="news-edit-form">
                  <div className="title-with-emoji">
                    <input value={editPostTitle} onChange={e => setEditPostTitle(e.target.value)} placeholder="Titel" className="news-title-input" data-testid="edit-news-title" />
                    <select value={editPostCategory} onChange={e => setEditPostCategory(e.target.value)} className="cat-select" data-testid="edit-news-category">
                      {categoryNames.filter(c => c !== 'Alle').map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button type="button" className="title-emoji-btn" onClick={() => { setEmojiTarget('edit-title'); setShowEmojiPicker(!showEmojiPicker); }} title="Emoji">😊</button>
                  </div>
                  <div className="news-format-bar">
                    <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('bold')} title="Fett"><Bold size={15} /></button>
                    <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('italic')} title="Kursiv"><Italic size={15} /></button>
                    <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('insertUnorderedList')} title="Aufzählung"><ListIcon size={15} /></button>
                    <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('insertOrderedList')} title="Nummerierung">1.</button>
                    <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => document.execCommand('formatBlock', false, 'h3')} title="Überschrift"><Type size={15} /></button>
                    <button type="button" className="fmt-btn" onMouseDown={e => e.preventDefault()} onClick={() => openUrlDialog('link', `edit-content-editor-${post.id}`, true)} title="Link einfuegen"><Link2 size={15} /></button>
                    <button type="button" className="fmt-btn" onClick={() => openUrlDialog('video', null, true)} title="YouTube einbetten"><Video size={15} /></button>
                    <button type="button" className="fmt-btn" onClick={() => handleUploadNewsVideo(true)} title="Video hochladen (max. 1000 MB, wird automatisch auf 720p optimiert)" data-testid="edit-news-upload-video-btn"><Film size={15} /></button>
                    <button type="button" className="fmt-btn emoji-fmt" onClick={() => { setEmojiTarget('edit-content'); setShowEmojiPicker(!showEmojiPicker); }} title="Emoji">😊</button>
                    <label className="fmt-btn" title="Bilder anhaengen (mehrere moeglich)">
                      <Camera size={15} />
                      <input type="file" accept="image/*" multiple onChange={e => handleUploadNewsImage(e, true)} hidden data-testid="edit-news-image-input" />
                    </label>
                    <label className="fmt-btn" title="Datei anhaengen">
                      <Upload size={15} />
                      <input type="file" onChange={async (e) => { const file = e.target.files[0]; if (!file) return; const input = e.target; try { const resp = await uploadFileChunked(file, API_URL, authHeaders, setUploadProg); setEditPostFiles(prev => [...prev, { url: resp.file_url, name: resp.file_name }]); } catch (err) { alertDialog(err.response?.data?.detail || 'Upload fehlgeschlagen. Bitte erneut versuchen.', { title: 'Upload fehlgeschlagen', danger: true }); } setUploadProg(null); input.value = ''; }} hidden />
                    </label>
                  </div>
                  <UploadProgress progress={uploadProg} />
                  <div className="news-input-wrap">
                    <div
                      id={`edit-content-editor-${post.id}`}
                      className="wysiwyg-editor"
                      contentEditable
                      data-testid="edit-news-editor"
                      ref={el => { if (el && !el.dataset.init) { el.innerHTML = DOMPurify.sanitize(post.content || '', { ADD_ATTR: ['target', 'rel'] }); el.dataset.init = '1'; } }}
                      onInput={e => setEditPostContent(e.currentTarget.innerHTML)}
                      onPaste={e => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
                    />
                    {showEmojiPicker && (emojiTarget === 'edit-title' || emojiTarget === 'edit-content') && (
                      <div className="emoji-picker-pro">
                        <div className="emoji-cat-tabs">
                          {Object.keys(EMOJI_CATS).map(cat => (
                            <button key={cat} type="button" className={`emoji-cat-tab ${emojiCat === cat ? 'active' : ''}`} onClick={() => setEmojiCat(cat)}>{cat}</button>
                          ))}
                        </div>
                        <div className="emoji-grid">
                          {EMOJI_CATS[emojiCat].map(em => <button key={em} type="button" className="emoji-btn" onClick={() => { if (emojiTarget === 'edit-title') { setEditPostTitle(prev => prev + em); } else { const editor = document.getElementById(`edit-content-editor-${post.id}`); if (editor) { editor.focus(); document.execCommand('insertText', false, em); setEditPostContent(editor.innerHTML); } } setShowEmojiPicker(false); }}>{em}</button>)}
                        </div>
                      </div>
                    )}
                  </div>
                  {editPostImages.length > 0 && (
                    <div className="news-images-preview" data-testid="edit-images-preview">
                      {editPostImages.map((img, i) => (
                        <div key={`eimg-${i}`} className="news-image-preview">
                          <img src={resolveUrl(img)} alt="Vorschau" />
                          <button type="button" className="remove-img-btn" onClick={() => setEditPostImages(prev => prev.filter((_, j) => j !== i))} data-testid={`remove-edit-image-${i}`}><X size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  {editPostVideos.length > 0 && (
                    <div className="news-videos-preview" data-testid="edit-videos-preview">
                      {editPostVideos.map((v, i) => (
                        <div key={`evid-${i}`} className="news-video-preview">
                          <span><Film size={13} /> {v.name}</span>
                          <button type="button" className="remove-img-btn" onClick={() => setEditPostVideos(prev => prev.filter((_, j) => j !== i))} data-testid={`remove-edit-video-${i}`}><X size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  {editPostVideo && (
                    <div className="news-video-preview">
                      <span>YouTube: {editPostVideo}</span>
                      <button type="button" className="remove-img-btn" onClick={() => setEditPostVideo('')}><X size={14} /></button>
                    </div>
                  )}
                  {editPostFiles.length > 0 && (
                    <div className="news-files-list">
                      {editPostFiles.map((f, i) => (
                        <div key={`efile-${i}-${f.name}`} className="multi-item"><Download size={12} /><span>{f.name}</span><button type="button" className="btn-icon-sm danger" onClick={() => setEditPostFiles(prev => prev.filter((_, j) => j !== i))}><X size={12} /></button></div>
                      ))}
                    </div>
                  )}
                  <div className="news-edit-actions">
                    <button className="btn-sm" data-testid="save-edit-news" onClick={() => { axios.put(`${API_URL}/api/community/news/${post.id}`, { title: editPostTitle, content: editPostContent, image_url: '', image_urls: editPostImages, video_url: editPostVideo, video_urls: editPostVideos, files: editPostFiles, category: editPostCategory }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } }).then(() => { setEditingPost(null); setShowEmojiPicker(false); localRefreshNews(); }); }}><Save size={14} /> Speichern</button>
                    <button className="btn-icon" onClick={() => { setEditingPost(null); setShowEmojiPicker(false); }}>Abbrechen</button>
                  </div>
                </div>
              ) : (
                <div className={`news-content-layout ${(postImages.length || postVideos.length || embedUrl) && !isExpanded ? 'has-media' : ''}`}>
                  <div className="news-text-col">
                    <SafeHtml className="news-body" html={DOMPurify.sanitize(renderContent(displayContent), {ADD_TAGS: ['iframe'], ADD_ATTR: ['allowfullscreen', 'frameborder', 'target', 'rel']})} />
                    {isLong && !isExpanded && <button className="news-expand-btn" onClick={() => setExpandedPosts(prev => ({...prev, [post.id]: true}))} data-testid={`expand-${post.id}`}>Alles anzeigen</button>}
                    {isExpanded && isLong && <button className="news-expand-btn" onClick={() => setExpandedPosts(prev => ({...prev, [post.id]: false}))}>Weniger anzeigen</button>}
                  </div>
                  {!isExpanded && (postImages.length > 0 || postVideos.length > 0 || embedUrl) && (
                    <div className="news-media-col">
                      {postImages.length > 0 && (
                        <div className="news-thumb-wrap" onClick={() => setExpandedPosts(prev => ({...prev, [post.id]: true}))} data-testid={`gallery-thumb-${post.id}`}>
                          <img src={resolveUrl(postImages[0])} alt="" className="news-post-image thumbnail" />
                          {postImages.length > 1 && <span className="gallery-more-badge">+{postImages.length - 1}</span>}
                        </div>
                      )}
                      {postVideos.length > 0 && (
                        <div className="news-video-thumb" onClick={() => setExpandedPosts(prev => ({...prev, [post.id]: true}))} data-testid={`upload-video-thumb-${post.id}`}>
                          <video src={`${resolveUrl(postVideos[0].url)}#t=0.1`} preload="metadata" muted playsInline className="video-thumb-vid" />
                          <div className="video-play-overlay"><Video size={24} /></div>
                          {postVideos.length > 1 && <span className="gallery-more-badge">+{postVideos.length - 1}</span>}
                        </div>
                      )}
                      {embedUrl && (() => { const thumb = getYoutubeThumbnail(post.video_url); return (
                        <div className="news-video-thumb" onClick={() => setExpandedPosts(prev => ({...prev, [post.id]: true}))} data-testid={`video-thumb-${post.id}`}>
                          {thumb && <img src={thumb} alt="Video" className="video-thumb-img" />}
                          <div className="video-play-overlay"><Video size={24} /></div>
                        </div>
                      ); })()}
                    </div>
                  )}
                </div>
              )}

              {isExpanded && postImages.length > 0 && (
                <div className={`news-gallery-grid ${postImages.length === 1 ? 'single' : ''}`} data-testid={`gallery-${post.id}`}>
                  {postImages.map((img, gi) => (
                    <img key={`g-${gi}`} src={resolveUrl(img)} alt="" onClick={() => setLightbox({ images: postImages, idx: gi })} data-testid={`gallery-img-${post.id}-${gi}`} />
                  ))}
                </div>
              )}
              {isExpanded && postVideos.map((v, vi) => (
                <video key={`pv-${vi}`} controls preload="metadata" playsInline className="news-video-player" src={resolveUrl(v.url)} data-testid={`news-video-player-${post.id}-${vi}`} />
              ))}
              {embedUrl && isExpanded && (
                <div className="news-video-embed">
                  <iframe src={embedUrl} title="Video" allowFullScreen frameBorder="0" />
                </div>
              )}

              {post.files && post.files.length > 0 && (
                <div className="news-files-display">
                  {post.files.map((f, i) => (
                    <a key={`file-${i}-${f.name}`} href={`${API_URL}${f.url}`} download={f.name} className="lesson-download" target="_blank" rel="noreferrer"><Download size={14} /> {f.name}</a>
                  ))}
                </div>
              )}

              <div className="news-social-bar">
                <button className={`social-btn ${post.likes?.includes(user?.id) ? 'liked' : ''}`} onClick={() => handleLikeNews(post.id)} data-testid={`like-${post.id}`}>
                  ❤️ {post.likes?.length || 0}
                </button>
                <button className={`social-btn comment-toggle ${expandedComments[post.id] ? 'active' : ''}`} onClick={() => setExpandedComments(prev => ({ ...prev, [post.id]: !prev[post.id] }))} data-testid={`toggle-comments-${post.id}`}>
                  <MessageSquare size={15} /> Kommentieren{post.comments?.length ? ` (${post.comments.length})` : ''}
                </button>
              </div>

              <div className="news-actions">
                {(post.author_id === user?.id || isAdminOrCoach) && (
                  <button onClick={() => { setEditingPost(post.id); setEditPostTitle(post.title || ''); setEditPostContent(post.content || ''); setEditPostImages(getPostImages(post)); setEditPostVideos(getPostVideos(post)); setEditPostVideo(post.video_url || ''); setEditPostFiles(post.files || []); setEditPostCategory(post.category || ''); setShowEmojiPicker(false); }} className="btn-icon" data-testid={`edit-news-${post.id}`}><Pencil size={14} /> Bearbeiten</button>
                )}
                {isAdminOrCoach && (
                  <button onClick={() => handlePinNews(post.id, !post.pinned)} className="btn-icon" data-testid={`pin-${post.id}`}>
                    {post.pinned ? <><PinOff size={14} /> Loslassen</> : <><Pin size={14} /> Anpinnen</>}
                  </button>
                )}
                {(post.author_id === user?.id || isAdminOrCoach) && (
                  <button onClick={() => handleDeleteNews(post.id)} className="btn-icon danger" data-testid={`delete-news-${post.id}`}><Trash2 size={14} /> Löschen</button>
                )}
              </div>

              {expandedComments[post.id] && (
                <div className="news-comments" data-testid={`comments-${post.id}`}>
                  {post.comments?.map((c, i) => {
                    const isEditingThis = editingComment?.commentId === c.id;
                    const mayEdit = canEditComment(c);
                    return (
                      <div key={c.id || i} className="comment-item" data-testid={`comment-${c.id || i}`}>
                        <div className="comment-head">
                          <div className="comment-author">
                            <AvatarWithCard userId={c.author_id} image={getAuthorImg(c.author_id, c) ? `${API_URL}${getAuthorImg(c.author_id, c)}` : null} size={20} API_URL={API_URL} authHeaders={authHeaders} avatarClass="comment-avatar" placeholderClass="comment-avatar-placeholder" />
                            <strong>{c.author_name}</strong>
                            <span className="comment-date">{c.created_at ? new Date(c.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}{c.updated_at ? ' (bearbeitet)' : ''}</span>
                          </div>
                          <div className="comment-actions">
                            {mayEdit && !isEditingThis && <button className="btn-icon-sm" onClick={() => { setEditingComment({ postId: post.id, commentId: c.id }); setEditCommentFiles(c.files || []); setCommentEmojiFor(null); }} title="Bearbeiten (bis 1 Std. nach Erstellung)" data-testid={`edit-comment-${c.id}`}><Pencil size={12} /></button>}
                            {(mayEdit || isAdminOrCoach) && <button className="btn-icon-sm danger" onClick={() => handleDeleteComment(post.id, c.id)} title="Kommentar löschen" data-testid={`delete-comment-${c.id}`}><Trash2 size={12} /></button>}
                          </div>
                        </div>
                        {isEditingThis ? (
                          <div className="comment-composer" data-testid={`comment-edit-composer-${c.id}`}>
                            {renderCommentToolbar(`edit-comment-editor-${c.id}`, post.id, true)}
                            <div id={`edit-comment-editor-${c.id}`} className="wysiwyg-editor comment-editor" contentEditable data-testid={`edit-comment-input-${c.id}`}
                              ref={el => { if (el && !el.dataset.init) { el.innerHTML = DOMPurify.sanitize(c.text || '', { ADD_ATTR: ['target', 'rel'] }); el.dataset.init = '1'; } }}
                              onPaste={e => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }} />
                            {renderCommentEmojiPicker(`edit-comment-editor-${c.id}`, post.id)}
                            {renderCommentFilesList(editCommentFiles, post.id, true)}
                            <UploadProgress progress={uploadProg} />
                            <div className="comment-composer-actions">
                              <button type="button" className="btn-icon" onClick={() => { setEditingComment(null); setCommentEmojiFor(null); }}>Abbrechen</button>
                              <button type="button" className="comment-send" onClick={() => handleSaveCommentEdit(post.id, c.id)} data-testid={`save-comment-${c.id}`}><Save size={13} /> Speichern</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="comment-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderContent(c.text), { ADD_ATTR: ['target', 'rel'] }) }} />
                            {(c.files || []).length > 0 && (
                              <div className="comment-files">
                                {c.files.map((f, fi) => {
                                  const fUrl = f.url && f.url.startsWith('/') ? `${API_URL}${f.url}` : f.url;
                                  return isImgFile(f) ? (
                                    <a key={`cimg-${fi}-${f.name}`} href={fUrl} target="_blank" rel="noreferrer" data-testid={`comment-img-${c.id}-${fi}`}><img src={fUrl} alt={f.name} className="comment-img-thumb" /></a>
                                  ) : (
                                    <a key={`cfile-${fi}-${f.name}`} href={fUrl} download={f.name} target="_blank" rel="noreferrer" className="lesson-download comment-file" data-testid={`comment-file-${c.id}-${fi}`}><Download size={12} /> {f.name}</a>
                                  );
                                })}
                              </div>
                            )}
                            <button className={`comment-like-btn ${c.likes?.includes(user?.id) ? 'liked' : ''}`} onClick={() => handleLikeComment(post.id, c.id)} data-testid={`like-comment-${c.id}`}>
                              ❤️ {c.likes?.length || 0}
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                  <div className="comment-composer" data-testid={`comment-composer-${post.id}`}>
                    {renderCommentToolbar(`comment-editor-${post.id}`, post.id, false)}
                    <div id={`comment-editor-${post.id}`} className="wysiwyg-editor comment-editor" contentEditable data-placeholder="Kommentar schreiben..." data-testid={`comment-input-${post.id}`}
                      onInput={e => { const val = e.currentTarget.innerText; setCommentText(prev => ({ ...prev, [post.id]: val })); }}
                      onPaste={e => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }} />
                    {renderCommentEmojiPicker(`comment-editor-${post.id}`, post.id)}
                    {renderCommentFilesList(commentFiles[post.id], post.id, false)}
                    <UploadProgress progress={uploadProg} />
                    <div className="comment-composer-actions">
                      <button onClick={() => handleCommentNews(post.id)} className="comment-send" disabled={!(commentText[post.id] || '').trim() && !(commentFiles[post.id] || []).length} data-testid={`comment-send-${post.id}`}><Send size={14} /> Senden</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Categories Management Modal */}
      <Modal show={showManageCategories} onClose={() => setShowManageCategories(false)} title="Kategorien verwalten">
        <div className="categories-manage">
          <div className="categories-list">
            {categories.map(cat => (
              <div key={cat.id} className="category-manage-item" data-testid={`cat-item-${cat.id}`}>
                <span>{cat.name}</span>
                <button className="btn-icon-sm danger" onClick={() => handleDeleteCategory(cat.id)} title="Löschen"><Trash2 size={13} /></button>
              </div>
            ))}
            {categories.length === 0 && <p className="hint-text">Noch keine Kategorien.</p>}
          </div>
          <div className="category-add-form">
            <input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="Neue Kategorie..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); }}} data-testid="new-category-input" />
            <button className="btn-sm" onClick={handleAddCategory} disabled={!newCategoryName.trim()} data-testid="add-category-btn"><Plus size={14} /> Hinzufuegen</button>
          </div>
        </div>
      </Modal>

      {lightbox && (
        <div className="news-lightbox" onClick={() => setLightbox(null)} data-testid="news-lightbox">
          <button type="button" className="news-lightbox-close" onClick={() => setLightbox(null)} data-testid="lightbox-close"><X size={20} /></button>
          {lightbox.images.length > 1 && (
            <button type="button" className="news-lightbox-nav prev" onClick={e => { e.stopPropagation(); setLightbox(lb => ({ ...lb, idx: (lb.idx - 1 + lb.images.length) % lb.images.length })); }} data-testid="lightbox-prev">‹</button>
          )}
          <img src={resolveUrl(lightbox.images[lightbox.idx])} alt="" onClick={e => e.stopPropagation()} />
          {lightbox.images.length > 1 && (
            <button type="button" className="news-lightbox-nav next" onClick={e => { e.stopPropagation(); setLightbox(lb => ({ ...lb, idx: (lb.idx + 1) % lb.images.length })); }} data-testid="lightbox-next">›</button>
          )}
        </div>
      )}

      {urlDialog && (
        <div className="modal-overlay" onClick={() => setUrlDialog(null)}>
          <div className="url-dialog-card" onClick={e => e.stopPropagation()} data-testid="url-dialog">
            <div className="url-dialog-head">
              {urlDialog.type === 'video' ? <Video size={18} /> : <Link2 size={18} />}
              <h4>{urlDialog.type === 'video' ? 'YouTube-Video einbetten' : 'Link einfügen'}</h4>
              <button type="button" className="url-dialog-close" onClick={() => setUrlDialog(null)} data-testid="url-dialog-close"><X size={16} /></button>
            </div>
            <p className="url-dialog-hint">{urlDialog.type === 'video' ? 'Füge die URL des YouTube-Videos ein, das im Beitrag angezeigt werden soll.' : 'Markierter Text wird mit diesem Link verknüpft.'}</p>
            <input
              autoFocus
              value={urlValue}
              onChange={e => setUrlValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmUrlDialog(); } }}
              placeholder={urlDialog.type === 'video' ? 'https://www.youtube.com/watch?v=...' : 'https://...'}
              className="url-dialog-input"
              data-testid="url-dialog-input"
            />
            <div className="url-dialog-actions">
              <button type="button" className="btn-icon" onClick={() => setUrlDialog(null)}>Abbrechen</button>
              <button type="button" className="btn-sm" disabled={!urlValue.trim()} onClick={confirmUrlDialog} data-testid="url-dialog-confirm">Einfügen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
